import { Commit, Dispatch } from 'vuex'
import { isApiError } from '@/types/api'
import {
  listPages as apiListPages,
  getPage as apiGetPage,
  createPage as apiCreatePage,
  updatePage as apiUpdatePage,
  deletePage as apiDeletePage,
  publishPage as apiPublishPage,
  unpublishPage as apiUnpublishPage,
  previewRunPage as apiPreviewRunPage,
  getPageRuns as apiGetPageRuns,
  deletePageRun as apiDeletePageRun,
  getPagePreviewToken as apiGetPagePreviewToken,
  getX402Networks as apiGetX402Networks,
  getPageCategories as apiGetPageCategories,
  getX402Settlements as apiGetX402Settlements,
  compilePageLayoutSchema as apiCompilePageLayoutSchema
} from '@/utils/api'
import type {
  PageDraftInput,
  PageLayout,
  PageRecord,
  PageRunRecord,
  PageSummary,
  PaymentSettlement,
  X402NetworkInfo
} from '@/types/pages'
import { publicPageDisplay } from '@/types/pages'

export interface PagesState {
  summaries: PageSummary[]
  activePage: PageRecord | null
  activeRuns: PageRunRecord[]
  activeRunsTotal: number
  networks: X402NetworkInfo[]
  /** §26 category suggestions (seeds ∪ in use) for the builder's picker. */
  categorySuggestions: string[]
  /** The owner's full x402 settlement ledger (agents + pages; cards filter by resourceId). */
  settlements: PaymentSettlement[]
  settlementsLoading: boolean
  /** Environment's `/p/` origin, supplied by the API with every pages response. */
  publicOrigin: string
  loadingList: boolean
  loadingPage: boolean
  loadingRuns: boolean
  saving: boolean
  publishing: boolean
  previewRunning: boolean
}

// Not browser-cached on purpose: pages are backend-authoritative and cheap to refetch.
export const pages = () => ({
  state: <PagesState>{
    summaries: [],
    activePage: null,
    activeRuns: [],
    activeRunsTotal: 0,
    networks: [],
    categorySuggestions: [],
    settlements: [],
    settlementsLoading: false,
    publicOrigin: '',
    loadingList: false,
    loadingPage: false,
    loadingRuns: false,
    saving: false,
    publishing: false,
    previewRunning: false
  },
  mutations: {
    setPageSummaries(state: PagesState, summaries: PageSummary[]) {
      state.summaries = summaries
    },
    removePageSummary(state: PagesState, id: string) {
      state.summaries = state.summaries.filter((summary) => summary.id !== id)
    },
    setActivePage(state: PagesState, page: PageRecord | null) {
      state.activePage = page
    },
    setActivePageRuns(
      state: PagesState,
      { runs, total, append }: { runs: PageRunRecord[]; total: number; append?: boolean }
    ) {
      state.activeRuns = append ? [...state.activeRuns, ...runs] : runs
      state.activeRunsTotal = total
    },
    clearActivePageRuns(state: PagesState) {
      state.activeRuns = []
      state.activeRunsTotal = 0
    },
    removeActivePageRun(state: PagesState, runId: string) {
      const before = state.activeRuns.length
      state.activeRuns = state.activeRuns.filter((run) => run.runId !== runId)
      if (state.activeRuns.length < before && state.activeRunsTotal > 0) {
        state.activeRunsTotal -= 1
      }
    },
    setPageNetworks(state: PagesState, networks: X402NetworkInfo[]) {
      state.networks = networks
    },
    setPageCategorySuggestions(state: PagesState, categories: string[]) {
      state.categorySuggestions = categories
    },
    setPageSettlements(state: PagesState, settlements: PaymentSettlement[]) {
      state.settlements = settlements
    },
    setPageSettlementsLoading(state: PagesState, value: boolean) {
      state.settlementsLoading = value
    },
    setPagesPublicOrigin(state: PagesState, publicOrigin: string | undefined) {
      // Ignore absent/empty values so a older API response can't wipe a known origin.
      if (typeof publicOrigin === 'string' && publicOrigin.trim() !== '') {
        state.publicOrigin = publicOrigin.trim().replace(/\/+$/, '')
      }
    },
    setPagesListLoading(state: PagesState, value: boolean) {
      state.loadingList = value
    },
    setActivePageLoading(state: PagesState, value: boolean) {
      state.loadingPage = value
    },
    setActivePageRunsLoading(state: PagesState, value: boolean) {
      state.loadingRuns = value
    },
    setPageSaving(state: PagesState, value: boolean) {
      state.saving = value
    },
    setPagePublishing(state: PagesState, value: boolean) {
      state.publishing = value
    },
    setPagePreviewRunning(state: PagesState, value: boolean) {
      state.previewRunning = value
    }
  },
  actions: {
    async fetchPages({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }): Promise<boolean> {
      commit('setPagesListLoading', true)
      try {
        const response = await apiListPages()
        if (isApiError(response)) {
          dispatch('addLogEvent', { title: 'Pages Fetch Error', content: response }, { root: true })
          return false
        }
        commit('setPageSummaries', response.pages ?? [])
        commit('setPagesPublicOrigin', response.publicOrigin)
        return true
      } finally {
        commit('setPagesListLoading', false)
      }
    },

    async fetchPage(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      id: string
    ): Promise<PageRecord | null> {
      commit('setActivePageLoading', true)
      try {
        const response = await apiGetPage(id)
        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Page Load Error: ${response.message}`, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'Page Load Error', content: response }, { root: true })
          return null
        }
        commit('setActivePage', response.page)
        commit('setPagesPublicOrigin', response.publicOrigin)
        return response.page
      } finally {
        commit('setActivePageLoading', false)
      }
    },

    async createPage(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      input: PageDraftInput
    ): Promise<PageRecord | null> {
      commit('setPageSaving', true)
      try {
        const response = await apiCreatePage(input)
        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Page Create Error: ${response.message}`, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'Page Create Error', content: response }, { root: true })
          return null
        }
        commit('setActivePage', response.page)
        commit('setPagesPublicOrigin', response.publicOrigin)
        dispatch('addLogEvent', { title: 'Page Created', content: { id: response.page.id, title: response.page.header.title } }, { root: true })
        void dispatch('fetchPages')
        return response.page
      } finally {
        commit('setPageSaving', false)
      }
    },

    async savePage(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { id, input }: { id: string; input: PageDraftInput }
    ): Promise<boolean> {
      commit('setPageSaving', true)
      try {
        const response = await apiUpdatePage(id, input)
        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Page Save Error: ${response.message}`, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'Page Save Error', content: response }, { root: true })
          return false
        }
        commit('setActivePage', response.page)
        commit('setPagesPublicOrigin', response.publicOrigin)
        dispatch('updateSnackbar', { text: 'Page saved', timeout: 3000 }, { root: true })
        dispatch('addLogEvent', { title: 'Page Saved', content: { id, title: response.page.header.title } }, { root: true })
        void dispatch('fetchPages')
        return true
      } finally {
        commit('setPageSaving', false)
      }
    },

    async deletePage(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: PagesState },
      id: string
    ): Promise<boolean> {
      const response = await apiDeletePage(id)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Page Delete Error: ${response.message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Page Delete Error', content: response }, { root: true })
        return false
      }
      commit('removePageSummary', id)
      if (state.activePage?.id === id) {
        commit('setActivePage', null)
        commit('clearActivePageRuns')
      }
      dispatch('updateSnackbar', { text: 'Page deleted', timeout: 3000 }, { root: true })
      dispatch('addLogEvent', { title: 'Page Deleted', content: id }, { root: true })
      return true
    },

    async publishPage(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      id: string
    ): Promise<boolean> {
      commit('setPagePublishing', true)
      try {
        const response = await apiPublishPage(id)
        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Page Publish Error: ${response.message}`, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'Page Publish Error', content: response }, { root: true })
          return false
        }
        commit('setActivePage', response.page)
        commit('setPagesPublicOrigin', response.publicOrigin)
        const slug = response.page.slug
        const isPrivate = response.page.visibility === 'private'
        dispatch(
          'updateSnackbar',
          {
            text: slug
              ? `Page published${isPrivate ? ' (private — only you can view it)' : ''} — ${publicPageDisplay(slug, response.publicOrigin)}`
              : 'Page published',
            timeout: 5000
          },
          { root: true }
        )
        dispatch('addLogEvent', { title: 'Page Published', content: { id, slug } }, { root: true })
        void dispatch('fetchPages')
        return true
      } finally {
        commit('setPagePublishing', false)
      }
    },

    async unpublishPage(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      id: string
    ): Promise<boolean> {
      commit('setPagePublishing', true)
      try {
        const response = await apiUnpublishPage(id)
        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Page Unpublish Error: ${response.message}`, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'Page Unpublish Error', content: response }, { root: true })
          return false
        }
        commit('setActivePage', response.page)
        dispatch('updateSnackbar', { text: 'Page unpublished', timeout: 3000 }, { root: true })
        dispatch('addLogEvent', { title: 'Page Unpublished', content: { id } }, { root: true })
        void dispatch('fetchPages')
        return true
      } finally {
        commit('setPagePublishing', false)
      }
    },

    async previewRunPage(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { id, input }: { id: string; input?: Record<string, string> }
    ): Promise<string | null> {
      commit('setPagePreviewRunning', true)
      try {
        const response = await apiPreviewRunPage(id, input)
        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Preview Run Error: ${response.message}`, color: 'error' }, { root: true })
          dispatch('addLogEvent', { title: 'Page Preview Run Error', content: response }, { root: true })
          return null
        }
        dispatch('addLogEvent', { title: 'Page Preview Run Started', content: { id, runId: response.runId } }, { root: true })
        return response.runId
      } finally {
        commit('setPagePreviewRunning', false)
      }
    },

    async fetchPageRuns(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { id, limit = 25, offset = 0, append = false }: { id: string; limit?: number; offset?: number; append?: boolean }
    ): Promise<boolean> {
      commit('setActivePageRunsLoading', true)
      try {
        const response = await apiGetPageRuns(id, limit, offset)
        if (isApiError(response)) {
          dispatch('addLogEvent', { title: 'Page Runs Fetch Error', content: response }, { root: true })
          return false
        }
        commit('setActivePageRuns', { runs: response.runs ?? [], total: response.total ?? 0, append })
        return true
      } finally {
        commit('setActivePageRunsLoading', false)
      }
    },

    async deletePageRun(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { id, runId }: { id: string; runId: string }
    ): Promise<boolean> {
      const response = await apiDeletePageRun(id, runId)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Run Delete Error: ${response.message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Page Run Delete Error', content: response }, { root: true })
        return false
      }
      commit('removeActivePageRun', runId)
      dispatch('updateSnackbar', { text: 'Run deleted', timeout: 3000 }, { root: true })
      dispatch('addLogEvent', { title: 'Page Run Deleted', content: { id, runId } }, { root: true })
      return true
    },

    async fetchPagePreviewToken(
      { dispatch }: { dispatch: Dispatch },
      id: string
    ): Promise<{ token: string; path: string } | null> {
      const response = await apiGetPagePreviewToken(id)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Preview Token Error: ${response.message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Page Preview Token Error', content: response }, { root: true })
        return null
      }
      return { token: response.token, path: response.path }
    },

    async compilePageLayoutSchema(
      { dispatch }: { dispatch: Dispatch },
      layout: PageLayout
    ): Promise<Record<string, unknown> | null> {
      const response = await apiCompilePageLayoutSchema(layout)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Layout Schema Error: ${response.message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Page Layout Schema Error', content: response }, { root: true })
        return null
      }
      return response.schema
    },

    async fetchPageSettlements({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }): Promise<boolean> {
      commit('setPageSettlementsLoading', true)
      try {
        const response = await apiGetX402Settlements()
        if (isApiError(response)) {
          dispatch('addLogEvent', { title: 'Settlements Fetch Error', content: response }, { root: true })
          return false
        }
        commit('setPageSettlements', response.settlements ?? [])
        return true
      } finally {
        commit('setPageSettlementsLoading', false)
      }
    },

    async fetchX402Networks({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }): Promise<boolean> {
      const response = await apiGetX402Networks()
      if (isApiError(response)) {
        dispatch('addLogEvent', { title: 'x402 Networks Fetch Error', content: response }, { root: true })
        return false
      }
      commit('setPageNetworks', response.networks ?? [])
      return true
    },

    async fetchPageCategorySuggestions({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }): Promise<boolean> {
      const response = await apiGetPageCategories()
      if (isApiError(response)) {
        dispatch('addLogEvent', { title: 'Page Categories Fetch Error', content: response }, { root: true })
        return false
      }
      commit('setPageCategorySuggestions', response.categories ?? [])
      return true
    },

    clearActivePage({ commit }: { commit: Commit }) {
      commit('setActivePage', null)
      commit('clearActivePageRuns')
    }
  },
  getters: {
    pageSummaries: (state: PagesState): PageSummary[] =>
      [...state.summaries].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
    pagesPublicOrigin: (state: PagesState): string => state.publicOrigin,
    activePage: (state: PagesState): PageRecord | null => state.activePage,
    activePageRuns: (state: PagesState): PageRunRecord[] => state.activeRuns,
    activePageRunsTotal: (state: PagesState): number => state.activeRunsTotal,
    x402PageNetworks: (state: PagesState): X402NetworkInfo[] => state.networks,
    pageCategorySuggestions: (state: PagesState): string[] => state.categorySuggestions,
    pageSettlements: (state: PagesState): PaymentSettlement[] => state.settlements,
    pageSettlementsLoading: (state: PagesState): boolean => state.settlementsLoading,
    pagesListLoading: (state: PagesState): boolean => state.loadingList,
    activePageLoading: (state: PagesState): boolean => state.loadingPage,
    activePageRunsLoading: (state: PagesState): boolean => state.loadingRuns,
    pageSaving: (state: PagesState): boolean => state.saving,
    pagePublishing: (state: PagesState): boolean => state.publishing,
    pagePreviewRunning: (state: PagesState): boolean => state.previewRunning
  }
})
